
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Surgical Phases Service
 * Uses backend API endpoints instead of direct Firestore access
 */
export const surgicalPhasesService = {
  // ===== PRE-OPERATIVE PHASE =====
  
  /**
   * Upload surgical consent form
   * @param {Object} consentData - Consent form data
   * @returns {Promise<Object>} Promise resolving to uploaded consent data
   */
  uploadConsentForm: async (consentData) => {
    try {
      console.log('Uploading consent form via API:', consentData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/consent-forms`,
        consentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Consent form uploaded successfully');
      return response.data.consentForm;
    } catch (error) {
      console.error('Error uploading consent form:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to upload consent form');
    }
  },

  /**
   * Mark consent as completed manually
   * @param {Object} consentData - Consent completion data
   * @returns {Promise<Object>} Promise resolving to completion record
   */
  markConsentComplete: async (consentData) => {
    try {
      console.log('Marking consent as complete via API:', consentData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/consent-completions`,
        consentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Consent marked as complete');
      return response.data.completionRecord;
    } catch (error) {
      console.error('Error marking consent as complete:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to mark consent as complete');
    }
  },

  /**
   * Create pre-op assessment
   * @param {Object} assessmentData - Assessment data
   * @returns {Promise<Object>} Promise resolving to assessment record
   */
  createPreOpAssessment: async (assessmentData) => {
    try {
      console.log('Creating pre-op assessment via API:', assessmentData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/assessments`,
        assessmentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Pre-op assessment created successfully');
      return response.data.assessment;
    } catch (error) {
      console.error('Error creating pre-op assessment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create pre-op assessment');
    }
  },

  /**
   * Schedule anesthesia consultation
   * @param {Object} consultationData - Consultation data
   * @returns {Promise<Object>} Promise resolving to appointment record
   */
  scheduleAnesthesiaConsultation: async (consultationData) => {
    try {
      console.log('Scheduling anesthesia consultation via API:', consultationData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/anesthesia-consultations`,
        consultationData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Anesthesia consultation scheduled successfully');
      return response.data.appointment;
    } catch (error) {
      console.error('Error scheduling anesthesia consultation:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to schedule anesthesia consultation');
    }
  },

  /**
   * Order pre-op labs
   * @param {Object} labData - Lab order data
   * @returns {Promise<Object>} Promise resolving to lab orders
   */
  orderPreOpLabs: async (labData) => {
    try {
      console.log('Ordering pre-op labs via API:', labData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/lab-orders`,
        labData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Pre-op labs ordered successfully');
      return response.data.labOrders;
    } catch (error) {
      console.error('Error ordering pre-op labs:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to order pre-op labs');
    }
  },

  /**
   * Document patient education
   * @param {Object} educationData - Education data
   * @returns {Promise<Object>} Promise resolving to education record
   */
  documentPatientEducation: async (educationData) => {
    try {
      console.log('Documenting patient education via API:', educationData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/patient-education`,
        educationData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Patient education documented successfully');
      return response.data.educationRecord;
    } catch (error) {
      console.error('Error documenting patient education:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to document patient education');
    }
  },

  /**
   * Create pre-op completion record
   * @param {Object} completionData - Completion data
   * @returns {Promise<Object>} Promise resolving to completion record
   */
  createPreOpCompletion: async (completionData) => {
    try {
      console.log('Creating pre-op completion via API:', completionData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/completions`,
        completionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Pre-op completion created successfully');
      return response.data.completionRecord;
    } catch (error) {
      console.error('Error creating pre-op completion:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create pre-op completion');
    }
  },

  /**
   * Create OR scheduling request
   * @param {Object} schedulingData - Scheduling request data
   * @returns {Promise<Object>} Promise resolving to scheduling request
   */
  createORSchedulingRequest: async (schedulingData) => {
    try {
      console.log('Creating OR scheduling request via API:', schedulingData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pre-op/or-scheduling-requests`,
        schedulingData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('OR scheduling request created successfully');
      return response.data.schedulingRequest;
    } catch (error) {
      console.error('Error creating OR scheduling request:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create OR scheduling request');
    }
  },

  // ===== INTRA-OPERATIVE PHASE =====

  /**
   * Start intra-op session
   * @param {Object} sessionData - Session data
   * @returns {Promise<Object>} Promise resolving to session record
   */
  startIntraOpSession: async (sessionData) => {
    try {
      console.log('Starting intra-op session via API:', sessionData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/sessions`,
        sessionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Intra-op session started successfully');
      return response.data.session;
    } catch (error) {
      console.error('Error starting intra-op session:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to start intra-op session');
    }
  },

  /**
   * Create surgical timeout record
   * @param {Object} timeoutData - Timeout data
   * @returns {Promise<Object>} Promise resolving to timeout record
   */
  createSurgicalTimeout: async (timeoutData) => {
    try {
      console.log('Creating surgical timeout via API:', timeoutData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/surgical-timeouts`,
        timeoutData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Surgical timeout created successfully');
      return response.data.timeoutRecord;
    } catch (error) {
      console.error('Error creating surgical timeout:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create surgical timeout');
    }
  },

  /**
   * Create anesthesia record
   * @param {Object} anesthesiaData - Anesthesia data
   * @returns {Promise<Object>} Promise resolving to anesthesia record
   */
  createAnesthesiaRecord: async (anesthesiaData) => {
    try {
      console.log('Creating anesthesia record via API:', anesthesiaData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/anesthesia-records`,
        anesthesiaData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Anesthesia record created successfully');
      return response.data.anesthesiaRecord;
    } catch (error) {
      console.error('Error creating anesthesia record:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create anesthesia record');
    }
  },

  /**
   * Create nursing flowsheet record
   * @param {Object} flowsheetData - Flowsheet data
   * @returns {Promise<Object>} Promise resolving to flowsheet record
   */
  createNursingFlowsheet: async (flowsheetData) => {
    try {
      console.log('Creating nursing flowsheet via API:', flowsheetData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/nursing-records`,
        flowsheetData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Nursing flowsheet created successfully');
      return response.data.flowsheetRecord;
    } catch (error) {
      console.error('Error creating nursing flowsheet:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create nursing flowsheet');
    }
  },

  /**
   * Create specimen tracking record
   * @param {Object} specimenData - Specimen data
   * @returns {Promise<Object>} Promise resolving to specimen record
   */
  createSpecimenTracking: async (specimenData) => {
    try {
      console.log('Creating specimen tracking via API:', specimenData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/specimens`,
        specimenData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Specimen tracking created successfully');
      return response.data.specimenRecord;
    } catch (error) {
      console.error('Error creating specimen tracking:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create specimen tracking');
    }
  },

  /**
   * Create implant tracking record
   * @param {Object} implantData - Implant data
   * @returns {Promise<Object>} Promise resolving to implant record
   */
  createImplantTracking: async (implantData) => {
    try {
      console.log('Creating implant tracking via API:', implantData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/implants`,
        implantData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Implant tracking created successfully');
      return response.data.implantRecord;
    } catch (error) {
      console.error('Error creating implant tracking:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create implant tracking');
    }
  },

  /**
   * Create operative note
   * @param {Object} noteData - Operative note data
   * @returns {Promise<Object>} Promise resolving to operative note
   */
  createOperativeNote: async (noteData) => {
    try {
      console.log('Creating operative note via API:', noteData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/operative-notes`,
        noteData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Operative note created successfully');
      return response.data.operativeNote;
    } catch (error) {
      console.error('Error creating operative note:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create operative note');
    }
  },

  /**
   * Create surgery completion record
   * @param {Object} completionData - Completion data
   * @returns {Promise<Object>} Promise resolving to completion record
   */
  createSurgeryCompletion: async (completionData) => {
    try {
      console.log('Creating surgery completion via API:', completionData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/intra-op/surgery-completions`,
        completionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Surgery completion created successfully');
      return response.data.completionRecord;
    } catch (error) {
      console.error('Error creating surgery completion:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create surgery completion');
    }
  },

  // ===== PACU PHASE =====

  /**
   * Create PACU assessment
   * @param {Object} assessmentData - Assessment data
   * @returns {Promise<Object>} Promise resolving to assessment record
   */
  createPACUAssessment: async (assessmentData) => {
    try {
      console.log('Creating PACU assessment via API:', assessmentData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/assessments`,
        assessmentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('PACU assessment created successfully');
      return response.data.assessment;
    } catch (error) {
      console.error('Error creating PACU assessment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create PACU assessment');
    }
  },

  /**
   * Create pain assessment
   * @param {Object} painData - Pain assessment data
   * @returns {Promise<Object>} Promise resolving to pain assessment
   */
  createPainAssessment: async (painData) => {
    try {
      console.log('Creating pain assessment via API:', painData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/pain-assessments`,
        painData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Pain assessment created successfully');
      return response.data.painAssessment;
    } catch (error) {
      console.error('Error creating pain assessment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create pain assessment');
    }
  },

  /**
   * Create vital signs record
   * @param {Object} vitalsData - Vital signs data
   * @returns {Promise<Object>} Promise resolving to vital signs record
   */
  createVitalSigns: async (vitalsData) => {
    try {
      console.log('Creating vital signs via API:', vitalsData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/vital-signs`,
        vitalsData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Vital signs created successfully');
      return response.data.vitalSigns;
    } catch (error) {
      console.error('Error creating vital signs:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create vital signs');
    }
  },

  /**
   * Create fluid balance record
   * @param {Object} fluidData - Fluid balance data
   * @returns {Promise<Object>} Promise resolving to fluid balance record
   */
  createFluidBalance: async (fluidData) => {
    try {
      console.log('Creating fluid balance via API:', fluidData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/fluid-balance`,
        fluidData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Fluid balance created successfully');
      return response.data.fluidBalance;
    } catch (error) {
      console.error('Error creating fluid balance:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create fluid balance');
    }
  },

  /**
   * Create Aldrete score
   * @param {Object} aldreteData - Aldrete score data
   * @returns {Promise<Object>} Promise resolving to Aldrete score
   */
  createAldreteScore: async (aldreteData) => {
    try {
      console.log('Creating Aldrete score via API:', aldreteData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/aldrete-scores`,
        aldreteData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Aldrete score created successfully');
      return response.data.aldreteScore;
    } catch (error) {
      console.error('Error creating Aldrete score:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create Aldrete score');
    }
  },

  /**
   * Create discharge clearance
   * @param {Object} clearanceData - Discharge clearance data
   * @returns {Promise<Object>} Promise resolving to discharge clearance
   */
  createDischargeClearance: async (clearanceData) => {
    try {
      console.log('Creating discharge clearance via API:', clearanceData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/discharge-clearances`,
        clearanceData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Discharge clearance created successfully');
      return response.data.dischargeClearance;
    } catch (error) {
      console.error('Error creating discharge clearance:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create discharge clearance');
    }
  },

  /**
   * Create transfer request
   * @param {Object} transferData - Transfer request data
   * @returns {Promise<Object>} Promise resolving to transfer request
   */
  createTransferRequest: async (transferData) => {
    try {
      console.log('Creating transfer request via API:', transferData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/pacu/transfer-requests`,
        transferData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Transfer request created successfully');
      return response.data.transferRequest;
    } catch (error) {
      console.error('Error creating transfer request:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create transfer request');
    }
  },

  // ===== POST-OPERATIVE PHASE =====

  /**
   * Create post-op orders
   * @param {Object} ordersData - Post-op orders data
   * @returns {Promise<Object>} Promise resolving to orders record
   */
  createPostOpOrders: async (ordersData) => {
    try {
      console.log('Creating post-op orders via API:', ordersData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/orders`,
        ordersData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Post-op orders created successfully');
      return response.data.ordersRecord;
    } catch (error) {
      console.error('Error creating post-op orders:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create post-op orders');
    }
  },

  /**
   * Create discharge instructions
   * @param {Object} instructionsData - Discharge instructions data
   * @returns {Promise<Object>} Promise resolving to instructions record
   */
  createDischargeInstructions: async (instructionsData) => {
    try {
      console.log('Creating discharge instructions via API:', instructionsData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/discharge-instructions`,
        instructionsData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Discharge instructions created successfully');
      return response.data.instructionsRecord;
    } catch (error) {
      console.error('Error creating discharge instructions:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create discharge instructions');
    }
  },

  /**
   * Schedule follow-up appointment
   * @param {Object} followUpData - Follow-up appointment data
   * @returns {Promise<Object>} Promise resolving to appointment record
   */
  scheduleFollowUp: async (followUpData) => {
    try {
      console.log('Scheduling follow-up via API:', followUpData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/follow-up-appointments`,
        followUpData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Follow-up appointment scheduled successfully');
      return response.data.appointment;
    } catch (error) {
      console.error('Error scheduling follow-up:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to schedule follow-up');
    }
  },

  /**
   * Create prescriptions
   * @param {Object} prescriptionsData - Prescriptions data
   * @returns {Promise<Object>} Promise resolving to prescriptions record
   */
  createPrescriptions: async (prescriptionsData) => {
    try {
      console.log('Creating prescriptions via API:', prescriptionsData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/prescriptions`,
        prescriptionsData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Prescriptions created successfully');
      return response.data.prescriptionsRecord;
    } catch (error) {
      console.error('Error creating prescriptions:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create prescriptions');
    }
  },

  /**
   * Create home care plan
   * @param {Object} homeCareData - Home care plan data
   * @returns {Promise<Object>} Promise resolving to home care plan
   */
  createHomeCarePlan: async (homeCareData) => {
    try {
      console.log('Creating home care plan via API:', homeCareData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/home-care-plans`,
        homeCareData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Home care plan created successfully');
      return response.data.homeCarePlan;
    } catch (error) {
      console.error('Error creating home care plan:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create home care plan');
    }
  },

  /**
   * Create patient education record
   * @param {Object} educationData - Patient education data
   * @returns {Promise<Object>} Promise resolving to education record
   */
  createPatientEducation: async (educationData) => {
    try {
      console.log('Creating patient education via API:', educationData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/patient-education`,
        educationData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Patient education created successfully');
      return response.data.educationRecord;
    } catch (error) {
      console.error('Error creating patient education:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create patient education');
    }
  },

  /**
   * Create post-op completion record
   * @param {Object} completionData - Completion data
   * @returns {Promise<Object>} Promise resolving to completion record
   */
  createPostOpCompletion: async (completionData) => {
    try {
      console.log('Creating post-op completion via API:', completionData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/completions`,
        completionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Post-op completion created successfully');
      return response.data.completionRecord;
    } catch (error) {
      console.error('Error creating post-op completion:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create post-op completion');
    }
  },

  /**
   * Create patient discharge record
   * @param {Object} dischargeData - Discharge data
   * @returns {Promise<Object>} Promise resolving to discharge record
   */
  createPatientDischarge: async (dischargeData) => {
    try {
      console.log('Creating patient discharge via API:', dischargeData.surgicalProcedureId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/post-op/patient-discharges`,
        dischargeData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Patient discharge created successfully');
      return response.data.dischargeRecord;
    } catch (error) {
      console.error('Error creating patient discharge:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create patient discharge');
    }
  },

  // ===== COMMON UTILITIES =====

  /**
   * Create print request
   * @param {Object} printData - Print request data
   * @returns {Promise<Object>} Promise resolving to print request
   */
  createPrintRequest: async (printData) => {
    try {
      console.log('Creating print request via API:', printData.documentType);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/print-requests`,
        printData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Print request created successfully');
      return response.data.printRequest;
    } catch (error) {
      console.error('Error creating print request:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create print request');
    }
  }
};
